/* WeQuote Dev Board — derived logic: working-day maths, phase model, the shared
 * card-status/risk read (cardStatus — PRD §5.6), checklist, request counting,
 * customer labelling, ranking. Ported verbatim from app.js; pure, no DOM. */
import type { Feature, Blockout, ReqGroup } from './types'
import { DEVCOLS, DL, SLGet, isProgress, isPool } from './constants'
import { daysUntil } from './dates'

export interface CardStatusRead { label: string; cls: string }

/* ── working-day maths (block-outs wired via setter; used by designDevRisk now,
 *    the full scheduler in the Roadmap phase) ── */
let _blockouts: Blockout[] = []
export function setScheduleBlockouts(b: Blockout[]): void { _blockouts = b || [] }

export function parseISO(iso: string): Date { return new Date(iso + 'T00:00:00') }
export function toISO(d: Date): string {
  const m = d.getMonth() + 1, day = d.getDate()
  return d.getFullYear() + '-' + (m < 10 ? '0' + m : m) + '-' + (day < 10 ? '0' + day : day)
}
export function isBlocked(iso: string, dev: string | null): boolean {
  return _blockouts.some((b) => (b.scope === 'all' || b.scope === dev) && iso >= b.start && iso <= b.end)
}
export function isWorkingDay(d: Date, dev: string | null): boolean {
  const w = d.getDay()
  if (w === 0 || w === 6) return false
  return !isBlocked(toISO(d), dev)
}
export function snapWorking(d: Date, dev: string | null): Date {
  const n = new Date(d.getTime())
  while (!isWorkingDay(n, dev)) n.setDate(n.getDate() + 1)
  return n
}
export function nextWorkingDay(d: Date, dev: string | null): Date {
  const n = new Date(d.getTime())
  do { n.setDate(n.getDate() + 1) } while (!isWorkingDay(n, dev))
  return n
}
export function addWorkingDays(startD: Date, dur: number, dev: string | null): Date {
  let n = snapWorking(startD, dev)
  let rem = Math.max(1, Math.ceil(dur)) - 1
  while (rem > 0) { n = nextWorkingDay(n, dev); rem-- }
  return n
}
export function subWorkingDays(dISO: string, n: number): Date {
  const d = parseISO(dISO)
  let rem = Math.max(0, Math.ceil(n) - 1)
  while (rem > 0) {
    do { d.setDate(d.getDate() - 1) } while (!isWorkingDay(d, null))
    rem--
  }
  return d
}

export function hasEstimate(f: Partial<Feature>): boolean {
  const d = parseFloat(f.time || '')
  return !isNaN(d) && d > 0
}
export function isLate(f: Partial<Feature>): boolean {
  return !!(f.estCompletion && f.deadline && f.estCompletion > f.deadline)
}

/* ── phase model ── */
export type Phase = 'review' | 'archived' | 'design' | 'dev' | 'plan'
export function activePhase(feat: Partial<Feature>): Phase {
  if (feat.status === 'needsreview') return 'review'
  if (feat.status === 'archived') return 'archived'
  if (feat.status === 'design' || feat.status === 'designcomplete') return 'design'
  if (isProgress(feat.status || '')) return 'dev' // dev columns, testing, on hold, live
  return 'plan' // backlog
}
export function statusDisplay(feat: Partial<Feature>): string {
  return DEVCOLS.indexOf(feat.status || '') > -1 ? 'In development' : SLGet(feat.status || '')
}
export function statusPillCls(feat: Partial<Feature>): string {
  return DEVCOLS.indexOf(feat.status || '') > -1 ? 's-indev' : 's-' + feat.status
}
export function phaseStripeCls(feat: Partial<Feature>): string {
  const s = feat.status || ''
  if (s === 'onhold') return 'ph-hold'
  if (s === 'testing' || s === 'testcomplete') return 'ph-testing'
  if (DEVCOLS.indexOf(s) > -1) return 'ph-dev'
  if (s === 'design' || s === 'designcomplete') return 'ph-design'
  return 'ph-plan'
}

/* ── design-runway risk (PRD §5.6): can the build still fit before the dev deadline? ── */
export function designDevRisk(feat: Partial<Feature>): '' | 'overdue' | 'atrisk' {
  if (!feat.deadline || !hasEstimate(feat)) return ''
  if (daysUntil(feat.deadline) !== null && (daysUntil(feat.deadline) as number) < 0) return 'overdue'
  const estDays = parseFloat(feat.time!)
  const earliestFinish = toISO(addWorkingDays(new Date(), estDays, feat.dev || null))
  if (earliestFinish > feat.deadline) return 'atrisk'
  const mustStart = toISO(subWorkingDays(feat.deadline, estDays))
  const dm = daysUntil(mustStart)
  if (dm !== null && dm <= 5 && (feat.designProgress || 0) < 100) return 'atrisk'
  if (feat.designDeadline && feat.designDeadline > mustStart) return 'atrisk'
  return ''
}

/* ── the ONE shared status/risk read (Focus, Roadmap, Calendar all use this) ── */
export function cardStatus(feat: Partial<Feature>): CardStatusRead {
  if (feat.status === 'onhold') return { label: 'On hold', cls: 'onhold' }
  if (feat.status === 'live') return { label: 'Shipped', cls: 'done' }
  const ph = activePhase(feat)
  if (ph === 'design') {
    const runway = designDevRisk(feat)
    if (runway === 'overdue') return { label: 'Overdue', cls: 'overdue' }
    if (runway === 'atrisk') return { label: 'At risk', cls: 'atrisk' }
    if (feat.status === 'designcomplete') return { label: 'Design done', cls: 'done' }
    const dp = feat.designProgress || 0
    const dd = feat.designDeadline
    if (!dd) return dp > 0 ? { label: 'In progress', cls: 'inprogress' } : { label: 'Not started', cls: 'notstarted' }
    const du = daysUntil(dd)
    if (du !== null && du < 0 && dp < 100) return { label: 'Overdue', cls: 'overdue' }
    if (dp <= 0) return { label: 'Not started', cls: 'notstarted' }
    if (du !== null && du <= 5 && dp < 60) return { label: 'At risk', cls: 'atrisk' }
    return { label: 'On track', cls: 'ontrack' }
  }
  const deadline = feat.deadline
  const progress = feat.progress || 0
  const projected = feat.estCompletion || ''
  if (!deadline) return progress > 0 ? { label: 'In progress', cls: 'inprogress' } : { label: 'Not started', cls: 'notstarted' }
  const du2 = daysUntil(deadline)
  if (du2 !== null && du2 < 0 && progress < 100) return { label: 'Overdue', cls: 'overdue' }
  if (progress <= 0) return { label: 'Not started', cls: 'notstarted' }
  const late = !!projected && projected > deadline
  if (late || (du2 !== null && du2 <= 5 && progress < 60)) return { label: 'At risk', cls: 'atrisk' }
  return { label: 'On track', cls: 'ontrack' }
}

/* ── checklist ── */
export function checklistProgress(feat: Partial<Feature>): { done: number; total: number } {
  const items = feat.checklist || []
  return { done: items.filter((c) => c.done).length, total: items.length }
}
export function checklistComplete(feat: Partial<Feature>): boolean {
  return (feat.checklist || []).every((c) => c.done) // empty list = complete
}

/* ── requests / customer labelling ── */
export function reqCount(feat: Partial<Feature>): number {
  const set: Record<string, true> = {}
  ;(feat.requests || []).forEach((r) => {
    const c = (r.company || '').trim().toLowerCase()
    if (c) set[c] = true
  })
  return Object.keys(set).length
}
export function custLabel(feat: Partial<Feature>): string {
  const manual = (feat.cust || '').trim()
  if (manual && manual !== 'Automatic') return manual
  const seen: string[] = []
  const set: Record<string, 1> = {}
  ;(feat.requests || []).forEach((r) => {
    const c = (r.company || '').trim()
    if (!c) return
    const k = c.toLowerCase()
    if (!set[k]) { set[k] = 1; seen.push(c) }
  })
  if (!seen.length) return 'All'
  if (seen.length === 1) return seen[0]
  return seen[0] + ' +' + (seen.length - 1)
}
export function reqGroups(feat: Partial<Feature>): ReqGroup[] {
  const map: Record<string, ReqGroup> = {}
  ;(feat.requests || []).forEach((r) => {
    const key = (r.company || '') + '||' + (r.person || '')
    if (!map[key]) map[key] = { company: r.company || '', person: r.person || '', dates: [], first: '', last: '', chases: 0 }
    if (r.date) map[key].dates.push(r.date)
  })
  const arr = Object.keys(map).map((k) => {
    const g = map[k]
    g.dates.sort()
    g.first = g.dates[0] || ''
    g.last = g.dates[g.dates.length - 1] || ''
    g.chases = Math.max(0, g.dates.length - 1)
    return g
  })
  arr.sort((a, b) => (a.first || '').localeCompare(b.first || ''))
  return arr
}
export function lastRequestedDate(feat: Partial<Feature>): string {
  let last = ''
  ;(feat.requests || []).forEach((r) => { if (r.date && r.date > last) last = r.date })
  return last
}
export function lastChaseDate(feat: Partial<Feature>): string {
  let last = ''
  reqGroups(feat).forEach((g) => {
    for (let i = 1; i < g.dates.length; i++) if (g.dates[i] > last) last = g.dates[i]
  })
  return last
}
export function requesterLabel(feat: Partial<Feature>): string {
  const s = feat.source
  if (s && s.type === 'chat') return 'Support chat · ' + (s.company || 'Unknown company') + (s.person ? ' · ' + s.person : '')
  if (s && s.type === 'manual') return 'Added by ' + (s.by || 'Unknown')
  return 'Origin not recorded'
}

/* ── customer-facing naming ── */
export function custDescOf(f: Partial<Feature>): string {
  return f.customerDesc && ('' + f.customerDesc).trim() ? ('' + f.customerDesc).trim() : ''
}
export function custDisplayName(f: Partial<Feature>): string {
  return f.customerName && ('' + f.customerName).trim() ? ('' + f.customerName).trim() : f.title || ''
}

/* ── ranking ── */
export function inPipelineF(f: Feature): boolean {
  return f.status !== 'live' && f.status !== 'needsreview' && f.status !== 'archived' && f.status !== 'idea'
}
/* ── sections (v2 §1): Committed → Design → Now → Next → Later. Priority
 * numbers run continuously by section, so each band holds a contiguous range. */
export function sectionOf(f: Partial<Feature>): string {
  const s = f.status || ''
  if (s === 'designcomplete' || DEVCOLS.indexOf(s) > -1 || s === 'custreview' || s === 'testing' || s === 'testcomplete' || s === 'onhold') return 'committed'
  if (s === 'design') return 'design'
  if (s === 'now') return 'now'
  if (s === 'next') return 'next'
  if (isPool(s)) return 'later' // later + legacy backlog
  return 'other'
}
const SECTION_RANK: Record<string, number> = { committed: 0, design: 1, now: 2, next: 3, later: 4, other: 5 }
export function sectionRank(f: Partial<Feature>): number { return SECTION_RANK[sectionOf(f)] }

export function rankMap(features: Feature[]): Record<number, number> {
  // Continuous 1…N over the grouped flow (Lee, 2 Sep): Committed first,
  // ordered by projected completion (delivery order), then Design → Now →
  // Next → Later by manual order. Never the raw stored po.
  const m: Record<number, number> = {}
  const pipe = features.filter(inPipelineF)
  const est = (f: Feature) => f.estCompletion || '9999-99'
  pipe.sort((a, b) => {
    const sr = sectionRank(a) - sectionRank(b)
    if (sr !== 0) return sr
    if (sectionOf(a) === 'committed') {
      const ae = est(a); const be = est(b)
      if (ae !== be) return ae < be ? -1 : 1
    }
    return a.po - b.po
  }).forEach((f, i) => { m[f.id] = i + 1 })
  return m
}
export function assignedName(dev: string): string {
  return dev && DL[dev] ? DL[dev] : ''
}

/* ── "needs attention" predicates (Overview tiles + due-date filter) ── */
export function isDesignOverdue(f: Partial<Feature>): boolean {
  return (isPool(f.status || '') || f.status === 'design') && !!f.designDeadline && (daysUntil(f.designDeadline!) as number) < 0
}
export function isDevOverdue(f: Partial<Feature>): boolean {
  const inDev = DEVCOLS.indexOf(f.status || '') > -1 || f.status === 'testing' || f.status === 'testcomplete' || f.status === 'onhold'
  return inDev && !!f.deadline && (daysUntil(f.deadline!) as number) < 0
}
export function isDesignStartSoon(f: Partial<Feature>): boolean {
  if (f.status !== 'design') return false
  const dd = daysUntil(f.designDeadline || '')
  if (dd !== null && dd <= 2) return true // design deadline within 2 days (or passed)
  const sd = daysUntil(f.start || '')
  if (sd !== null && sd >= 0 && sd <= 2) return true // dev start date within 2 days
  if (f.deadline && f.time) { // must-start-by = deadline − est dev time
    const days = parseFloat(f.time)
    if (!isNaN(days)) {
      const dl = new Date(f.deadline + 'T00:00:00')
      if (!isNaN(dl.getTime())) {
        const t = new Date(); t.setHours(0, 0, 0, 0)
        const mustStart = Math.round((dl.getTime() - days * 86400000 - t.getTime()) / 86400000)
        if (mustStart <= 2) return true
      }
    }
  }
  return false
}
